import type { BoardColumn } from "./boardColumns";
import { generateColumnId } from "./boardColumns";
import type { ParsedReservationRow } from "./useReservationImport";

/** Champs fixes connus de l'app — ils alimentent un calcul (couverts, CA) ou une règle (anti-doublon). */
export type ChampFixe =
  | 'lastName' | 'firstName' | 'fullName' | 'phone' | 'email' | 'time'
  | 'covers' | 'tableNumber' | 'comment' | 'sourceRef';

/** 'ignore' | 'new' | 'existing:<id colonne du tableau>' | un champ fixe. */
export type MappingTarget = ChampFixe | 'ignore' | 'new' | `existing:${string}`;

export const CHAMPS_FIXES: { value: ChampFixe; label: string }[] = [
  { value: 'lastName', label: 'Nom' },
  { value: 'firstName', label: 'Prénom' },
  { value: 'fullName', label: 'Nom complet (une seule colonne)' },
  { value: 'phone', label: 'Téléphone' },
  { value: 'email', label: 'Email' },
  { value: 'time', label: 'Heure' },
  { value: 'covers', label: 'Nombre de couverts (adultes)' },
  { value: 'tableNumber', label: 'Table' },
  { value: 'comment', label: 'Commentaire' },
  { value: 'sourceRef', label: 'Référence de réservation (anti-doublon)' },
];

export interface FichierReservationsLu {
  fileName: string;
  columns: string[];
  rows: Record<string, any>[];
}

// Même principe de lecture que l'import Convives (src/components/Evenementiel/convives/convivesFileParser.ts) :
// aucun format d'export de plateforme de réservation (Zenchef ou autre) n'est connu à l'avance, donc rien n'est
// deviné côté colonnes au-delà d'une proposition, toujours modifiable avant le moindre envoi au serveur.
const cleanKey = (k: string) => k.replace(/^﻿/, '').trim().toLowerCase();
const findCol = (keys: string[], ...candidates: string[]) => keys.find((k) => candidates.some((c) => cleanKey(k) === c.toLowerCase())) ?? null;

const lireSelonEncodage = (file: File, buf: ArrayBuffer): [any, any] => {
  const estTexte = /\.(csv|txt|tsv)$/i.test(file.name) || /^text\//.test(file.type || '');
  if (!estTexte) return [new Uint8Array(buf), { type: 'array' }];
  try {
    const texte = new TextDecoder('utf-8', { fatal: true }).decode(buf);
    return [texte, { type: 'string' }];
  } catch {
    return [new Uint8Array(buf), { type: 'array' }];
  }
};

/** Lit le fichier dans le navigateur (jamais envoyé brut au serveur). */
export async function lireFichierReservations(file: File): Promise<FichierReservationsLu> {
  const buf = await file.arrayBuffer();
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(...lireSelonEncodage(file, buf));
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  if (rows.length === 0) throw new Error('Le fichier est vide.');
  return { fileName: file.name, columns: Object.keys(rows[0]), rows };
}

/**
 * Propose une association par défaut, colonne par colonne :
 *  1. un champ fixe reconnu par son en-tête (Nom, Téléphone, Heure...) ;
 *  2. sinon une colonne personnalisée déjà présente sur CE tableau, si le libellé correspond
 *     (chacun configure ses colonnes comme il veut — une colonne "Statut" déjà créée à la main
 *     doit être retrouvée, pas dupliquée) ;
 *  3. sinon la création d'une nouvelle colonne portant le nom de l'en-tête — jamais "ignorer" par
 *     défaut, pour qu'une donnée du fichier ne disparaisse pas silencieusement (demande d'Emeni,
 *     25/09) : l'utilisateur choisit ensuite d'accepter cette colonne ou de l'ignorer à l'étape 2.
 */
export function proposerMapping(columns: string[], boardColumns: BoardColumn[]): Record<string, MappingTarget> {
  const colNom = findCol(columns, 'nom', 'last_name', 'lastname');
  const colPrenom = findCol(columns, 'prénom', 'prenom', 'first_name', 'firstname');
  const colNomComplet = !colNom && !colPrenom ? findCol(columns, 'client', 'nom complet', 'name', 'réservataire', 'reservataire', 'customer') : null;
  const colPhone = findCol(columns, 'téléphone', 'telephone', 'tel', 'phone', 'mobile');
  const colEmail = findCol(columns, 'email', 'e-mail', 'mail', 'courriel');
  const colTime = findCol(columns, 'heure', 'time', 'heure de réservation', 'heure resa');
  const colCovers = findCol(columns, 'couverts', 'nombre de couverts', 'personnes', 'nb personnes', 'pax', 'party size', 'guests', 'convives');
  const colTable = findCol(columns, 'table', 'n° table', 'numero table', 'numéro de table');
  const colComment = findCol(columns, 'commentaire', 'comment', 'note', 'notes', 'remarque', 'remarques');
  const colRef = findCol(columns, 'référence', 'reference', 'ref', 'id', 'id réservation', 'booking id', 'réservation id');

  const editableColumns = boardColumns.filter((c) => !c.locked && !c.field);
  const findExisting = (key: string) => editableColumns.find((c) => cleanKey(c.label) === cleanKey(key));

  const mapping: Record<string, MappingTarget> = {};
  columns.forEach((k) => {
    if (k === colNom) { mapping[k] = 'lastName'; return; }
    if (k === colPrenom) { mapping[k] = 'firstName'; return; }
    if (k === colNomComplet) { mapping[k] = 'fullName'; return; }
    if (k === colPhone) { mapping[k] = 'phone'; return; }
    if (k === colEmail) { mapping[k] = 'email'; return; }
    if (k === colTime) { mapping[k] = 'time'; return; }
    if (k === colCovers) { mapping[k] = 'covers'; return; }
    if (k === colTable) { mapping[k] = 'tableNumber'; return; }
    if (k === colComment) { mapping[k] = 'comment'; return; }
    if (k === colRef) { mapping[k] = 'sourceRef'; return; }
    const existing = findExisting(k);
    mapping[k] = existing ? `existing:${existing.id}` : 'new';
  });
  return mapping;
}

export interface MappingResultat {
  rows: ParsedReservationRow[];
  /** Nouvelles colonnes à créer sur le tableau avant d'envoyer les lignes (une par en-tête mappé sur 'new'). */
  nouvellesColonnes: BoardColumn[];
}

/**
 * Applique l'association choisie : une ligne par réservation, valeurs nettoyées, plus la liste des
 * colonnes personnalisées à créer. boardId entre dans la référence anti-doublon synthétique : sans
 * colonne de référence dans le fichier, deux réservations de deux services différents mais au même
 * nom/heure ne doivent pas se faire passer pour un doublon l'une de l'autre — la contrainte d'unicité
 * en base ne porte que sur (client, source, référence), pas sur le tableau.
 */
export function appliquerMappingReservations(
  lu: FichierReservationsLu,
  mapping: Record<string, MappingTarget>,
  boardId: number,
  boardColumns: BoardColumn[],
): MappingResultat {
  const colOf = (champ: ChampFixe) => lu.columns.find((k) => mapping[k] === champ) || null;
  const cNom = colOf('lastName'), cPrenom = colOf('firstName'), cNomComplet = colOf('fullName');
  const cPhone = colOf('phone'), cEmail = colOf('email'), cTime = colOf('time'), cCovers = colOf('covers');
  const cTable = colOf('tableNumber'), cComment = colOf('comment'), cRef = colOf('sourceRef');

  // Une nouvelle colonne par en-tête mappé sur 'new' — id stable généré une seule fois, en tenant
  // compte des colonnes déjà créées dans cette même passe (deux en-têtes ne peuvent pas revendiquer le même id).
  const nouvellesColonnes: BoardColumn[] = [];
  const idParColonneFichier: Record<string, string> = {};
  lu.columns.forEach((k) => {
    if (mapping[k] !== 'new') return;
    const id = generateColumnId(k, [...boardColumns, ...nouvellesColonnes]);
    idParColonneFichier[k] = id;
    nouvellesColonnes.push({ id, label: k.replace(/^﻿/, '').trim(), type: 'texte', visible: true, locked: false });
  });

  const rows: ParsedReservationRow[] = lu.rows.map((r) => {
    const lastName = cNom ? String(r[cNom] || '').trim() : (cNomComplet ? String(r[cNomComplet] || '').trim() : '');
    const firstName = cPrenom ? String(r[cPrenom] || '').trim() : '';
    const phone = cPhone ? String(r[cPhone] || '').trim() : '';
    const time = cTime ? String(r[cTime] || '').trim() : '';
    const adults = cCovers ? Number(r[cCovers]) || 0 : 0;
    const explicitRef = cRef ? String(r[cRef] || '').trim() : '';
    const sourceRef = explicitRef || `csv:${boardId}:${lastName.toLowerCase()}|${firstName.toLowerCase()}|${phone}|${time}`;

    const customValues: Record<string, string> = {};
    lu.columns.forEach((k) => {
      const target = mapping[k];
      if (target === 'new') customValues[idParColonneFichier[k]] = String(r[k] ?? '').trim();
      else if (target.startsWith('existing:')) customValues[target.slice('existing:'.length)] = String(r[k] ?? '').trim();
    });

    return {
      lastName, firstName, phone,
      email: cEmail ? String(r[cEmail] || '').trim() : '',
      time, adults,
      tableNumber: cTable ? String(r[cTable] || '').trim() : '',
      comment: cComment ? String(r[cComment] || '').trim() : '',
      sourceRef,
      customValues,
    };
  });

  return { rows, nouvellesColonnes };
}

/** Lettre de colonne à la façon d'un tableur : A, B, … Z, AA. */
export const lettreColonneReservation = (i: number): string => {
  let n = i, s = '';
  do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
  return s;
};
